/**
 * Pixel image-size toolbar control for CKEditor 5.
 *
 * Keeps CKEditor's existing percentage resize system intact and writes pixel
 * sizing through the existing `resizeImage` command. Only width is persisted;
 * height is derived from the image aspect ratio.
 */

import Plugin from '@ckeditor/ckeditor5-core/src/plugin.js';
import View from '@ckeditor/ckeditor5-ui/src/view.js';
import InputTextView from '@ckeditor/ckeditor5-ui/src/inputtext/inputtextview.js';
import ButtonView from '@ckeditor/ckeditor5-ui/src/button/buttonview.js';
import ImageUtils from '@ckeditor/ckeditor5-image/src/imageutils.js';

import './imagepixelsize.css';

const MIN_WIDTH_PX = 30;
const MAX_WIDTH_PX = 600;

export default class ImagePixelSize extends Plugin {
  static get requires() {
    return [ImageUtils];
  }

  static get pluginName() {
    return 'ImagePixelSize';
  }

  init() {
    const editor = this.editor;

    editor.ui.componentFactory.add('imagePixelSize', locale => {
      return new ImagePixelSizeView(locale, editor);
    });
  }
}

class ImagePixelSizeView extends View {
  constructor(locale, editor) {
    super(locale);

    this.editor = editor;
    this.imageUtils = editor.plugins.get('ImageUtils');
    this._aspectRatio = null;
    this._hasSelectedImage = false;
    this._syncFrame = null;

    this.widthInput = this._createInput('Image width in pixels (30 to 600)', 'W');
    this.heightInput = this._createInput('Image height in pixels (aspect ratio locked)', 'H');

    this.applyButton = new ButtonView(locale);
    this.applyButton.set({
      label: 'Apply',
      withText: true,
      tooltip: 'Apply pixel size'
    });
    this.applyButton.extendTemplate({
      attributes: {
        class: ['ck-image-pixel-size__apply']
      }
    });

    this.children = this.createCollection();
    this.children.add(this._createTextView('W', 'ck-image-pixel-size__axis'));
    this.children.add(this.widthInput);
    this.children.add(this._createTextView('px', 'ck-image-pixel-size__unit'));
    this.children.add(this._createTextView('×', 'ck-image-pixel-size__separator'));
    this.children.add(this._createTextView('H', 'ck-image-pixel-size__axis'));
    this.children.add(this.heightInput);
    this.children.add(this._createTextView('px', 'ck-image-pixel-size__unit'));
    this.children.add(this._createLockView());
    this.children.add(this.applyButton);

    this.setTemplate({
      tag: 'div',
      attributes: {
        class: ['ck', 'ck-image-pixel-size'],
        'aria-label': 'Image pixel size'
      },
      children: this.children
    });

    this.listenTo(this.widthInput, 'input', () => this._handleWidthInput());
    this.listenTo(this.heightInput, 'input', () => this._handleHeightInput());
    this.listenTo(this.applyButton, 'execute', () => this._applyPixelWidth());

    this.listenTo(editor.model.document.selection, 'change:range', () => {
      this._scheduleSync();
    });

    this.listenTo(editor.model.document, 'change:data', () => {
      this._scheduleSync();
    });
  }

  render() {
    super.render();

    // CKEditor toolbars prevent mousedown by default. Stop propagation inside
    // this custom control so the text inputs can receive focus normally.
    this._onMouseDown = event => event.stopPropagation();
    this._onKeyDown = event => {
      if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        this._applyPixelWidth();
        return;
      }

      // Do not let the parent toolbar use arrow keys while the user is editing
      // a numeric value in either field.
      if (event.key.startsWith('Arrow')) {
        event.stopPropagation();
      }
    };

    this.element.addEventListener('mousedown', this._onMouseDown);
    this.element.addEventListener('keydown', this._onKeyDown);

    this._scheduleSync();
  }

  destroy() {
    if (this._syncFrame !== null) {
      window.cancelAnimationFrame(this._syncFrame);
      this._syncFrame = null;
    }

    if (this.element) {
      this.element.removeEventListener('mousedown', this._onMouseDown);
      this.element.removeEventListener('keydown', this._onKeyDown);
    }

    super.destroy();
  }

  focus() {
    this.widthInput.focus();
  }

  _createInput(title, placeholder) {
    const input = new InputTextView(this.locale);

    input.set({
      value: '',
      inputMode: 'numeric',
      placeholder
    });

    input.extendTemplate({
      attributes: {
        class: ['ck-image-pixel-size__input'],
        autocomplete: 'off',
        spellcheck: 'false',
        maxlength: '4',
        title,
        'aria-label': title
      }
    });

    return input;
  }

  _createTextView(text, className) {
    const view = new View(this.locale);

    view.setTemplate({
      tag: 'span',
      attributes: {
        class: ['ck', className]
      },
      children: [text]
    });

    return view;
  }

  _createLockView() {
    const view = new View(this.locale);

    view.setTemplate({
      tag: 'span',
      attributes: {
        class: ['ck', 'ck-image-pixel-size__lock'],
        title: 'Aspect ratio locked',
        'aria-label': 'Aspect ratio locked',
        role: 'img'
      },
      children: ['🔒']
    });

    return view;
  }

  _scheduleSync() {
    if (!this.element) {
      return;
    }

    if (this._syncFrame !== null) {
      window.cancelAnimationFrame(this._syncFrame);
    }

    this._syncFrame = window.requestAnimationFrame(() => {
      this._syncFrame = null;
      this._syncFromSelectedImage();
    });
  }

  _syncFromSelectedImage() {
    const metrics = this._getSelectedImageMetrics();

    if (!metrics) {
      this._hasSelectedImage = false;
      this._aspectRatio = null;
      this.widthInput.value = '';
      this.heightInput.value = '';
      this.widthInput.hasError = false;
      this.heightInput.hasError = false;
      this.applyButton.isEnabled = false;
      return;
    }

    this._hasSelectedImage = true;
    this._aspectRatio = metrics.aspectRatio;
    this.widthInput.value = String(metrics.width);
    this.heightInput.value = String(metrics.height);

    this._updateValidationState();
  }

  _getSelectedImageMetrics() {
    const editor = this.editor;
    const modelImage = this.imageUtils.getClosestSelectedImageElement(
      editor.model.document.selection
    );

    if (!modelImage) {
      return null;
    }

    const viewElement = editor.editing.mapper.toViewElement(modelImage);
    if (!viewElement) {
      return null;
    }

    const viewImg = this.imageUtils.findViewImgElement(viewElement);
    if (!viewImg) {
      return null;
    }

    const domImg = editor.editing.view.domConverter.mapViewToDom(viewImg);
    if (!domImg) {
      return null;
    }

    const rect = domImg.getBoundingClientRect();
    const renderedWidth = rect.width;
    const renderedHeight = rect.height;

    const aspectRatio =
      domImg.naturalWidth > 0 && domImg.naturalHeight > 0
        ? domImg.naturalWidth / domImg.naturalHeight
        : renderedWidth > 0 && renderedHeight > 0
          ? renderedWidth / renderedHeight
          : null;

    if (!aspectRatio || !Number.isFinite(aspectRatio) || aspectRatio <= 0) {
      return null;
    }

    // If the image is already persisted in px, show that exact model value.
    // For %, original, or other existing values, show its current rendered px width.
    const modelWidth = modelImage.getAttribute('width');
    const pixelModelWidth = this._parsePixelModelWidth(modelWidth);
    const width = pixelModelWidth || Math.round(renderedWidth);

    if (!width || width <= 0) {
      return null;
    }

    return {
      width,
      height: Math.max(1, Math.round(width / aspectRatio)),
      aspectRatio
    };
  }

  _parsePixelModelWidth(modelWidth) {
    if (typeof modelWidth !== 'string' || !modelWidth.trim().endsWith('px')) {
      return null;
    }

    const numericValue = Number(modelWidth.trim().slice(0, -2));

    return Number.isFinite(numericValue) && numericValue > 0
      ? Math.round(numericValue)
      : null;
  }

  _handleWidthInput() {
    const width = this._parsePositiveInteger(this.widthInput.element.value);

    if (width !== null && this._aspectRatio) {
      this.heightInput.value = String(
        Math.max(1, Math.round(width / this._aspectRatio))
      );
    }

    this._updateValidationState();
  }

  _handleHeightInput() {
    const height = this._parsePositiveInteger(this.heightInput.element.value);

    if (height !== null && this._aspectRatio) {
      this.widthInput.value = String(
        Math.max(1, Math.round(height * this._aspectRatio))
      );
    }

    this._updateValidationState();
  }

  _updateValidationState() {
    const width = this._parsePositiveInteger(this.widthInput.element?.value ?? this.widthInput.value);
    const height = this._parsePositiveInteger(this.heightInput.element?.value ?? this.heightInput.value);

    const widthIsValid =
      width !== null && width >= MIN_WIDTH_PX && width <= MAX_WIDTH_PX;
    const heightIsValid = height !== null && height > 0;

    this.widthInput.hasError = this._hasSelectedImage && !widthIsValid;
    this.heightInput.hasError = this._hasSelectedImage && !heightIsValid;
    this.applyButton.isEnabled =
      this._hasSelectedImage && widthIsValid && heightIsValid;
  }

  _applyPixelWidth() {
    const width = this._parsePositiveInteger(this.widthInput.element?.value);

    if (
      !this._hasSelectedImage ||
      width === null ||
      width < MIN_WIDTH_PX ||
      width > MAX_WIDTH_PX
    ) {
      this._updateValidationState();
      return;
    }

    const command = this.editor.commands.get('resizeImage');
    if (!command || !command.isEnabled) {
      return;
    }

    // Use CKEditor's existing single width model. Do not persist height or a
    // second size system; this keeps px and % resizing mutually consistent.
    this.editor.execute('resizeImage', {
      width: `${width}px`
    });

    this.editor.editing.view.focus();
    this._scheduleSync();
  }

  _parsePositiveInteger(value) {
    const normalized = String(value ?? '').trim();

    if (!/^\d+$/.test(normalized)) {
      return null;
    }

    const number = Number(normalized);

    return Number.isSafeInteger(number) && number > 0 ? number : null;
  }
}
